import { useEffect, useState } from 'react'
import { extractImageColors, ExtractedColor } from 'src/shared/utils/extractImageColors'

interface UseImageColorsOptions {
  imageUrl: string | null
  colorTolerance?: number
  maxColors?: number
  enabled?: boolean
}

interface UseImageColorsReturn {
  colors: ExtractedColor[]
  isLoading: boolean
  error: Error | null
  colorCount: number
}

/**
 * Hook to extract colors from an image
 */
export const useImageColors = ({
  imageUrl,
  colorTolerance = 30,
  maxColors = 20,
  enabled = true,
}: UseImageColorsOptions): UseImageColorsReturn => {
  const [colors, setColors] = useState<ExtractedColor[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!imageUrl || !enabled) {
      setColors([])
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    extractImageColors(imageUrl, colorTolerance, maxColors)
      .then((extractedColors) => {
        setColors(extractedColors)
        setIsLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error('Failed to extract colors'))
        setIsLoading(false)
        setColors([])
      })
  }, [imageUrl, colorTolerance, maxColors, enabled])

  return {
    colors,
    isLoading,
    error,
    colorCount: colors.length,
  }
}

